// RTK Query API. Only generic endpoints: resolved schema, widget data, widget action, AI, prefs.
import { createApi, fetchBaseQuery, type BaseQueryFn, type FetchArgs, type FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import type { RootState } from './store';
import { logout } from './authSlice';
import type { DashboardSchema, Shell } from './types';

// A public read-only link (/s/<token>): every request goes to that link's read-only API instead,
// without a sign-in; anything the link can't do (actions, settings, preferences) is refused here.
let shareToken: string | null = null;
export function setShareToken(token: string) {
  shareToken = token;
}

function forShareLink(args: string | FetchArgs): string | FetchArgs | null {
  const url = typeof args === 'string' ? args : args.url;
  const base = `api/public/${encodeURIComponent(shareToken!)}`;
  const mapped =
    url === 'api/schema/resolved'
      ? `${base}/shell`
      : url.startsWith('api/schema/resolved/dashboards/')
        ? `${base}/dashboards/${url.slice('api/schema/resolved/dashboards/'.length).split('?')[0]}`
        : url === 'api/widgets/data'
          ? `${base}/data`
          : url === 'api/time'
            ? `${base}/time`
            : null;
  if (!mapped) return null;
  return typeof args === 'string' ? mapped : { ...args, url: mapped };
}

const raw = fetchBaseQuery({
  baseUrl: '/',
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as RootState).auth.token;
    if (token && !shareToken) headers.set('authorization', `Bearer ${token}`);
    return headers;
  },
});

const baseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (args, api, extra) => {
  if (shareToken) {
    const shared = forShareLink(args);
    if (!shared) return { error: { status: 403, data: { error: 'This is a read-only view' } } as FetchBaseQueryError };
    return raw(shared, api, extra);
  }
  const res = await raw(args, api, extra);
  if (res.error?.status === 401) api.dispatch(logout());
  return res;
};

export interface WidgetDataArgs {
  dashboardId: string;
  widgetId: string;
  filters: Record<string, unknown>;
}

export const api = createApi({
  reducerPath: 'api',
  baseQuery,
  tagTypes: ['Schema', 'Widget'],
  endpoints: (b) => ({
    shell: b.query<Shell, void>({ query: () => 'api/schema/resolved', providesTags: ['Schema'] }),
    dashboard: b.query<DashboardSchema, { id: string; store?: string | null }>({
      query: ({ id, store }) => `api/schema/resolved/dashboards/${encodeURIComponent(id)}${store ? `?store=${encodeURIComponent(store)}` : ''}`,
      providesTags: ['Schema'],
    }),
    widgetData: b.query<{ data: any; generatedAt: string }, WidgetDataArgs>({
      query: (body) => ({ url: 'api/widgets/data', method: 'POST', body }),
      providesTags: (_r, _e, a) => [{ type: 'Widget', id: `${a.dashboardId}/${a.widgetId}` }, { type: 'Widget', id: a.dashboardId }],
      keepUnusedDataFor: 30,
    }),
    widgetAction: b.mutation<any, WidgetDataArgs & { action: string; rowId?: string | number | null; input?: unknown }>({
      query: (body) => ({ url: 'api/widgets/action', method: 'POST', body }),
      invalidatesTags: (_r, _e, a) => [{ type: 'Widget', id: a.dashboardId }],
      // "Authorise" actions (OAuth) answer with the platform's consent page; leave the app for it.
      async onQueryStarted(_args, { queryFulfilled }) {
        const res = await queryFulfilled.catch(() => null);
        const url = res?.data?.redirectUrl;
        if (typeof url === 'string' && url.startsWith('https://')) window.location.assign(url);
      },
    }),
    ask: b.mutation<any, { question: string }>({ query: (body) => ({ url: 'api/ai/ask', method: 'POST', body }) }),
    feedback: b.mutation<any, { id: string; feedback: 'up' | 'down' }>({ query: (body) => ({ url: 'api/ai/feedback', method: 'POST', body }) }),
    savePrefs: b.mutation<any, Record<string, unknown>>({ query: (body) => ({ url: 'api/me/prefs', method: 'PUT', body }), invalidatesTags: ['Schema'] }),
    setupStatus: b.query<{ needsSetup: boolean; currencies: string[]; branding?: Pick<Shell['branding'], 'productName' | 'logo'> }, void>({ query: () => 'api/setup/status' }),
    // The server's clock; `offsetMs` is how far it is ahead of this device's clock.
    serverTime: b.query<{ now: string; timeZone: string; offsetMs: number }, void>({
      query: () => 'api/time',
      transformResponse: (r: { now: string; timeZone: string }) => ({ ...r, offsetMs: Date.parse(r.now) - Date.now() }),
    }),
    setup: b.mutation<{ token: string }, { workspaceName: string; name: string; email: string; password: string; reportingCurrency: string }>({
      query: (body) => ({ url: 'api/setup', method: 'POST', body }),
    }),
    login: b.mutation<{ token: string }, { email: string; password: string }>({ query: (body) => ({ url: 'api/auth/login', method: 'POST', body }) }),
  }),
});

export const {
  useShellQuery,
  useDashboardQuery,
  useWidgetDataQuery,
  useWidgetActionMutation,
  useAskMutation,
  useFeedbackMutation,
  useSavePrefsMutation,
  useSetupStatusQuery,
  useServerTimeQuery,
  useSetupMutation,
  useLoginMutation,
} = api;

export function errorMessage(err: unknown): string {
  const e = err as any;
  if (!e) return 'Something went wrong';
  if (typeof e.data === 'object' && e.data?.error) {
    const issues = Array.isArray(e.data.issues) ? e.data.issues.map((i: any) => `${i.layer}: ${i.path ? `${i.path} — ` : ''}${i.message}`).join('\n') : '';
    return issues ? `${e.data.error}\n${issues}` : e.data.error;
  }
  if (e.status === 'FETCH_ERROR') return 'Cannot reach the server';
  return e.error ?? e.message ?? 'Something went wrong';
}
